import moment from 'moment'

import { TripGenerationMode } from '@/app/constants/delegation/tripGenerationMode'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { GpsFromTachographModel } from '@/app/models/gpsFromTachographModel'
import { TmsDataModel } from '@/app/models/tmsDataModel'
import { TIME_FORMAT } from '@/app/constants/date'
import { CountryModel } from '@/app/models/timesheet/countryModel'

export type CreatorGenerationButtonMode = '1-day' | '7-day' | '30-day'

export const HOMELAND_COUNTRY: CountryModel = {
  code: 'pl',
  name: 'Polska',
  nationId: 40,
  id: '1',
}

export const mapButtonModeToTripGenerationMode = (
  mode: CreatorGenerationButtonMode,
): TripGenerationMode => {
  switch (mode) {
    case '1-day':
      return TripGenerationMode.Daily
    case '7-day':
      return TripGenerationMode.Weekly
    case '30-day':
      return TripGenerationMode.MonthManualEntry
    default:
      return TripGenerationMode.Daily
  }
}

const mapTmsToEvents = (tmsData: TmsDataModel[]): EventModel[] => {
  const events: EventModel[] = []
  const existingDates: string[] = []

  tmsData.forEach((item) => {
    if (item.loadingDate && item.loadingCountry) {
      const loadingDateStr = moment(item.loadingDate).format(TIME_FORMAT)
      if (!existingDates.includes(loadingDateStr)) {
        events.push({
          date: moment(item.loadingDate),
          tripEvent: TripEventType.Loading,
          country: item.loadingCountry,
          docNumber: item.docNumber || undefined,
          tmsRouteType: item.routeType,
          secondDate: item.unloadingDate ? moment(item.unloadingDate) : null,
          secondCountry: item.unloadingCountry || null,
          lp: 0,
        })
        existingDates.push(loadingDateStr)
      }
    }

    if (item.unloadingDate && item.unloadingCountry) {
      const unloadingDateStr = moment(item.unloadingDate).format(TIME_FORMAT)
      if (!existingDates.includes(unloadingDateStr)) {
        events.push({
          date: moment(item.unloadingDate),
          tripEvent: TripEventType.Unloading,
          country: item.unloadingCountry,
          docNumber: item.docNumber || undefined,
          tmsRouteType: item.routeType,
          secondDate: item.loadingDate ? moment(item.loadingDate) : null,
          secondCountry: item.loadingCountry || null,
          lp: 0,
        })
        existingDates.push(unloadingDateStr)
      }
    }
  })

  return events
}

const mapBordersToEvents = (
  bordersData: GpsFromTachographModel[],
): EventModel[] => {
  return bordersData
    .filter((item) => item.date && item.country)
    .map((item) => ({
      date: moment(item.date),
      tripEvent: TripEventType.ChangeCountry,
      country: item.country,
      isTransit: item.isTransit || false,
      lp: 0,
    }))
}

export const buildGenerationEvents = ({
  tmsData,
  bordersData,
}: {
  tmsData: TmsDataModel[]
  bordersData: GpsFromTachographModel[]
}): EventModel[] => {
  const merged = [...mapTmsToEvents(tmsData), ...mapBordersToEvents(bordersData)]

  const sorted = merged.sort((a, b) => {
    if (a.date.isBefore(b.date)) return -1
    if (a.date.isAfter(b.date)) return 1
    return 0
  })

  return sorted.map((event, index) => ({
    ...event,
    lp: index,
  }))
}

export const mergeAndSortGenerationEvents = ({
  importedEvents,
  generatedEvents,
}: {
  importedEvents: EventModel[]
  generatedEvents: EventModel[]
}): EventModel[] => {
  const merged = [...importedEvents, ...generatedEvents]
    .filter((event) => event?.date)
  const uniqueEvents = new Map<string, EventModel>()

  merged.forEach((event) => {
    const key = [
      event.tripEvent,
      moment(event.date).toISOString(),
      event.country?.id ?? '',
      event.docNumber ?? '',
      event.isTransit ? '1' : '0',
      event.secondCountry?.id ?? '',
      event.secondDate ? moment(event.secondDate).toISOString() : '',
    ].join('|')

    if (!uniqueEvents.has(key)) {
      uniqueEvents.set(key, event)
    }
  })

  const sorted = Array.from(uniqueEvents.values()).sort(
    (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
  )

  return sorted.map((event, index) => ({
    ...event,
    lp: index,
  }))
}
